/**
 * Domain-specific error classes for the crypto module.
 *
 * Each error has a unique `name` so callers can `instanceof`-check or match
 * on the discriminator. None of these errors carry plaintext key material
 * or message content in their `message` strings — only opaque identifiers.
 *
 * Suite-related errors live in `./suite.ts` to keep this file dependency-free.
 */
/**
 * Thrown when an HPKE/AEAD open call fails because the wrong private key was
 * presented. The underlying library throws OpenError/DecapError; we wrap them
 * to keep the public surface stable across library versions.
 */
export class WrongKeyError extends Error {
    name = 'WrongKeyError';
    constructor(operation, cause) {
        super(`Decryption failed during ${operation}: provided key cannot decrypt this ciphertext.`);
        if (cause !== undefined) {
            // Preserve cause chain for log forensics without leaking key material.
            this.cause = cause;
        }
    }
}
/**
 * Thrown when an open call's associated-data binding does not match the
 * ciphertext's seal binding. In practice this means a caller tried to open
 * a CEK envelope with the wrong message_id (ciphertext-transplant attempt).
 */
export class BindingMismatchError extends Error {
    name = 'BindingMismatchError';
    constructor(expected, cause) {
        super(`Decryption failed: ciphertext is bound to a different context (expected '${expected}'). ` +
            `Possible ciphertext transplant or wrong message_id/vault_id.`);
        if (cause !== undefined) {
            this.cause = cause;
        }
    }
}
/**
 * Thrown when a KMS request returns AccessDenied or the IAM principal is
 * not authorised for the requested CMK. The original AWS SDK error code is
 * preserved on `cause` so the IAM team can debug, but we never leak the
 * raw AWS error message into application logs (it can include account IDs).
 */
export class KmsAccessDeniedError extends Error {
    name = 'KmsAccessDeniedError';
    keyAlias;
    constructor(keyAlias, cause) {
        super(`KMS denied access to key alias '${keyAlias}'. Verify the task role IAM policy.`);
        this.keyAlias = keyAlias;
        if (cause !== undefined) {
            this.cause = cause;
        }
    }
}
/**
 * Thrown when the platform's Web Crypto API is missing — for example when
 * this module is loaded into a Node version below 20 or a hardened sandbox
 * that has stripped `globalThis.crypto.subtle`. The crypto module cannot
 * function without SubtleCrypto, so this is fatal.
 */
export class CryptoEnvironmentError extends Error {
    name = 'CryptoEnvironmentError';
    constructor(missing) {
        super(`Crypto runtime requirement not met: ${missing} is unavailable. ` +
            `Node 20+ or a modern browser is required.`);
    }
}
/**
 * Thrown when input bytes have the wrong length for a given crypto primitive
 * (e.g. a "32-byte" X25519 public key that is not 32 bytes). This is always
 * a programmer error, never a runtime data error, but we throw rather than
 * assert to keep the failure visible in production.
 */
export class InvalidKeyMaterialError extends Error {
    name = 'InvalidKeyMaterialError';
    constructor(what, expected, actual) {
        super(`Invalid ${what}: expected ${expected} bytes, got ${actual}.`);
    }
}
